import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Skeleton } from '../components/ui/skeleton';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Progress } from '../components/ui/progress';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../components/ui/dialog';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import {
  Bell,
  Plus,
  Edit,
  Trash2,
  Send,
  Eye,
  FileText,
  RefreshCw,
  Calendar,
  Users,
  CheckCircle2,
  Clock,
  Paperclip,
  X,
  TrendingUp,
  AlertCircle,
  Mail,
  Newspaper
} from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AdminNoticesPage = () => {
  const { token } = useAuth();
  const headers = { Authorization: `Bearer ${token}` };

  const [notices, setNotices] = useState([]);
  const [categories, setCategories] = useState([]);
  const [investors, setInvestors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('published');
  
  // Dialog states
  const [createDialog, setCreateDialog] = useState(false);
  const [viewDialog, setViewDialog] = useState({ open: false, notice: null });
  const [deleteDialog, setDeleteDialog] = useState({ open: false, notice: null });
  
  // Form state
  const [formData, setFormData] = useState({
    title: '',
    content_html: '',
    category: 'general',
    related_loan_reference: '',
    is_global: true,
    recipient_ids: [],
    attachments: []
  });
  const [submitting, setSubmitting] = useState(false);

  const getCategoryConfig = (categoryId) => {
    const configs = {
      draw_down: { color: 'bg-blue-100 text-blue-700 border-blue-200', icon: TrendingUp },
      maturity: { color: 'bg-purple-100 text-purple-700 border-purple-200', icon: CheckCircle2 },
      rate: { color: 'bg-orange-100 text-orange-700 border-orange-200', icon: AlertCircle },
      general: { color: 'bg-gray-100 text-gray-700 border-gray-200', icon: Mail },
      newsletter: { color: 'bg-green-100 text-green-700 border-green-200', icon: Newspaper }
    };
    return configs[categoryId] || configs.general;
  };

  // Fetch data
  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [noticesRes, catsRes, investorsRes] = await Promise.all([
        axios.get(`${API_URL}/api/admin/notices`, { headers }),
        axios.get(`${API_URL}/api/notices/categories`, { headers }),
        axios.get(`${API_URL}/api/admin/notices/investors`, { headers })
      ]);
      
      setNotices(noticesRes.data);
      setCategories(catsRes.data);
      setInvestors(investorsRes.data);
    } catch (error) {
      console.error('Failed to fetch data:', error);
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString('en-ZA', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // Create notice
  const handleCreate = async (publish = false) => {
    if (!formData.title.trim() || !formData.content_html.trim()) {
      toast.error('Title and content are required');
      return;
    }

    setSubmitting(true);
    try {
      // Create the notice
      const createRes = await axios.post(`${API_URL}/api/admin/notices`, {
        title: formData.title,
        content_html: formData.content_html,
        category: formData.category,
        related_loan_reference: formData.related_loan_reference || null,
        is_global: formData.is_global,
        recipient_ids: formData.is_global ? null : formData.recipient_ids,
        attachment_names: formData.attachments
      }, { headers });

      const noticeId = createRes.data.notice_id;

      // Publish if requested
      if (publish) {
        await axios.post(`${API_URL}/api/admin/notices/${noticeId}/publish`, null, { headers });
        toast.success('Notice published and sent to investors!');
      } else {
        toast.success('Notice saved as draft');
      }

      setCreateDialog(false);
      resetForm();
      fetchData();
    } catch (error) {
      toast.error('Failed to create notice');
    } finally {
      setSubmitting(false);
    }
  };

  // Publish draft notice
  const handlePublish = async (noticeId) => {
    try {
      await axios.post(`${API_URL}/api/admin/notices/${noticeId}/publish`, null, { headers });
      toast.success('Notice published!');
      fetchData();
    } catch (error) {
      toast.error('Failed to publish notice');
    }
  };

  // Delete notice
  const handleDelete = async () => {
    if (!deleteDialog.notice) return;

    try {
      await axios.delete(`${API_URL}/api/admin/notices/${deleteDialog.notice.id}`, { headers });
      toast.success('Notice deleted');
      setDeleteDialog({ open: false, notice: null });
      fetchData();
    } catch (error) {
      toast.error('Failed to delete notice');
    }
  };

  // View notice detail
  const handleViewDetail = async (notice) => {
    try {
      const response = await axios.get(`${API_URL}/api/admin/notices/${notice.id}`, { headers });
      setViewDialog({ open: true, notice: response.data });
    } catch (error) {
      toast.error('Failed to load notice details');
    }
  };

  const resetForm = () => {
    setFormData({
      title: '',
      content_html: '',
      category: 'general',
      related_loan_reference: '',
      is_global: true,
      recipient_ids: [],
      attachments: []
    });
  };

  const addAttachment = () => {
    const fileName = prompt('Enter attachment file name (e.g., document.pdf):');
    if (fileName) {
      setFormData(prev => ({
        ...prev,
        attachments: [...prev.attachments, fileName]
      }));
    }
  };

  const removeAttachment = (index) => {
    setFormData(prev => ({
      ...prev,
      attachments: prev.attachments.filter((_, i) => i !== index)
    }));
  };

  const publishedNotices = notices.filter(n => n.status === 'published');
  const draftNotices = notices.filter(n => n.status === 'draft');

  // Summary stats
  const totalRecipients = publishedNotices.reduce((sum, n) => sum + n.total_recipients, 0);
  const totalRead = publishedNotices.reduce((sum, n) => sum + n.read_count, 0);
  const overallReadRate = totalRecipients > 0 ? Math.round((totalRead / totalRecipients) * 100) : 0;

  return (
    <div className="space-y-6" data-testid="admin-notices-page">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-[#212529] font-['Manrope']">Manage Notices</h1>
          <p className="text-[#6C757D] mt-1">Create and manage investor communications</p>
        </div>
        <div className="flex gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={fetchData}
            className="border-gray-200"
          >
            <RefreshCw className="h-4 w-4" />
          </Button>
          <Button
            className="bg-[#1D2D2A] hover:bg-[#243D37] text-white"
            onClick={() => { resetForm(); setCreateDialog(true); }}
            data-testid="create-notice-btn"
          >
            <Plus className="h-4 w-4 mr-2" />
            Create Notice
          </Button>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card className="border-0 shadow-sm">
          <CardContent className="pt-6">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-blue-100 flex items-center justify-center">
                <Bell className="h-5 w-5 text-blue-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-[#212529]">{publishedNotices.length}</p>
                <p className="text-xs text-[#6C757D]">Published</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="border-0 shadow-sm">
          <CardContent className="pt-6">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-amber-100 flex items-center justify-center">
                <Clock className="h-5 w-5 text-amber-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-[#212529]">{draftNotices.length}</p>
                <p className="text-xs text-[#6C757D]">Drafts</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="border-0 shadow-sm">
          <CardContent className="pt-6">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-green-100 flex items-center justify-center">
                <Users className="h-5 w-5 text-green-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-[#212529]">{totalRecipients}</p>
                <p className="text-xs text-[#6C757D]">Total Recipients</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="border-0 shadow-sm">
          <CardContent className="pt-6">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-purple-100 flex items-center justify-center">
                <CheckCircle2 className="h-5 w-5 text-purple-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-[#212529]">{overallReadRate}%</p>
                <p className="text-xs text-[#6C757D]">Read Rate</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Notices Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-4">
        <TabsList className="bg-gray-100 p-1">
          <TabsTrigger value="published" className="data-[state=active]:bg-white">
            Published ({publishedNotices.length})
          </TabsTrigger>
          <TabsTrigger value="drafts" className="data-[state=active]:bg-white">
            Drafts ({draftNotices.length})
          </TabsTrigger>
        </TabsList>

        {/* Published Notices */}
        <TabsContent value="published">
          <Card className="border-0 shadow-sm">
            <CardContent className="p-0">
              {loading ? (
                <div className="p-6 space-y-4">
                  {[1, 2, 3].map(i => <Skeleton key={i} className="h-16 w-full" />)}
                </div>
              ) : publishedNotices.length === 0 ? (
                <div className="p-12 text-center">
                  <Bell className="h-12 w-12 mx-auto text-gray-300 mb-4" />
                  <p className="text-[#6C757D]">No published notices yet</p>
                </div>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow className="bg-gray-50">
                      <TableHead>Notice</TableHead>
                      <TableHead>Category</TableHead>
                      <TableHead>Published</TableHead>
                      <TableHead className="text-center">Recipients</TableHead>
                      <TableHead className="text-center">Read Rate</TableHead>
                      <TableHead className="text-right">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {publishedNotices.map((notice) => {
                      const config = getCategoryConfig(notice.category);
                      const Icon = config.icon;
                      return (
                        <TableRow key={notice.id}>
                          <TableCell>
                            <div className="max-w-[300px]">
                              <p className="font-medium text-[#212529] truncate">{notice.title}</p>
                              {notice.related_loan_reference && (
                                <p className="text-xs text-[#6C757D]">{notice.related_loan_reference}</p>
                              )}
                            </div>
                          </TableCell>
                          <TableCell>
                            <Badge className={`${config.color} border`}>
                              <Icon className="h-3 w-3 mr-1" />
                              {notice.category.replace('_', ' ')}
                            </Badge>
                          </TableCell>
                          <TableCell className="text-[#6C757D] text-sm">
                            {formatDate(notice.published_date)}
                          </TableCell>
                          <TableCell className="text-center">{notice.total_recipients}</TableCell>
                          <TableCell>
                            <div className="flex items-center gap-2">
                              <Progress value={notice.read_percentage} className="h-2 w-16" />
                              <span className="text-sm text-[#6C757D]">{notice.read_percentage}%</span>
                            </div>
                          </TableCell>
                          <TableCell className="text-right">
                            <div className="flex items-center justify-end gap-2">
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => handleViewDetail(notice)}
                              >
                                <Eye className="h-4 w-4" />
                              </Button>
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => setDeleteDialog({ open: true, notice })}
                                className="text-red-600 hover:text-red-700"
                              >
                                <Trash2 className="h-4 w-4" />
                              </Button>
                            </div>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Draft Notices */}
        <TabsContent value="drafts">
          <Card className="border-0 shadow-sm">
            <CardContent className="p-0">
              {loading ? (
                <div className="p-6 space-y-4">
                  {[1, 2].map(i => <Skeleton key={i} className="h-16 w-full" />)}
                </div>
              ) : draftNotices.length === 0 ? (
                <div className="p-12 text-center">
                  <Clock className="h-12 w-12 mx-auto text-gray-300 mb-4" />
                  <p className="text-[#6C757D]">No draft notices</p>
                </div>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow className="bg-gray-50">
                      <TableHead>Notice</TableHead>
                      <TableHead>Category</TableHead>
                      <TableHead>Created</TableHead>
                      <TableHead className="text-right">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {draftNotices.map((notice) => {
                      const config = getCategoryConfig(notice.category);
                      const Icon = config.icon;
                      return (
                        <TableRow key={notice.id}>
                          <TableCell>
                            <p className="font-medium text-[#212529]">{notice.title}</p>
                          </TableCell>
                          <TableCell>
                            <Badge className={`${config.color} border`}>
                              <Icon className="h-3 w-3 mr-1" />
                              {notice.category.replace('_', ' ')}
                            </Badge>
                          </TableCell>
                          <TableCell className="text-[#6C757D] text-sm">
                            {formatDate(notice.created_at)}
                          </TableCell>
                          <TableCell className="text-right">
                            <div className="flex items-center justify-end gap-2">
                              <Button
                                size="sm"
                                className="bg-[#1D2D2A] hover:bg-[#243D37] text-white"
                                onClick={() => handlePublish(notice.id)}
                              >
                                <Send className="h-4 w-4 mr-1" />
                                Publish
                              </Button>
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => setDeleteDialog({ open: true, notice })}
                                className="text-red-600 hover:text-red-700"
                              >
                                <Trash2 className="h-4 w-4" />
                              </Button>
                            </div>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Create Notice Dialog */}
      <Dialog open={createDialog} onOpenChange={setCreateDialog}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-['Manrope']">Create New Notice</DialogTitle>
            <DialogDescription>
              Compose a notice to send to investors
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            {/* Title */}
            <div className="space-y-2">
              <label className="text-sm font-medium text-[#212529]">Title *</label>
              <Input
                placeholder="Enter notice title..."
                value={formData.title}
                onChange={(e) => setFormData(prev => ({ ...prev, title: e.target.value }))}
                className="border-gray-200"
                data-testid="notice-title-input"
              />
            </div>

            {/* Category */}
            <div className="space-y-2">
              <label className="text-sm font-medium text-[#212529]">Category *</label>
              <Select
                value={formData.category}
                onValueChange={(value) => setFormData(prev => ({ ...prev, category: value }))}
              >
                <SelectTrigger className="border-gray-200" data-testid="category-select">
                  <SelectValue placeholder="Select category" />
                </SelectTrigger>
                <SelectContent>
                  {categories.map((cat) => (
                    <SelectItem key={cat.id} value={cat.id}>{cat.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Related Loan Reference */}
            <div className="space-y-2">
              <label className="text-sm font-medium text-[#212529]">Related Loan Reference (optional)</label>
              <Input
                placeholder="e.g., PTC-2024-001"
                value={formData.related_loan_reference}
                onChange={(e) => setFormData(prev => ({ ...prev, related_loan_reference: e.target.value }))}
                className="border-gray-200"
              />
            </div>

            {/* Content */}
            <div className="space-y-2">
              <label className="text-sm font-medium text-[#212529]">Content (HTML) *</label>
              <Textarea
                placeholder="Enter notice content... (HTML supported)"
                value={formData.content_html}
                onChange={(e) => setFormData(prev => ({ ...prev, content_html: e.target.value }))}
                rows={8}
                className="border-gray-200 font-mono text-sm"
                data-testid="notice-content-input"
              />
              <p className="text-xs text-[#6C757D]">
                Tip: Use HTML tags like &lt;h2&gt;, &lt;p&gt;, &lt;ul&gt;, &lt;li&gt;, &lt;strong&gt; for formatting
              </p>
            </div>

            {/* Attachments */}
            <div className="space-y-2">
              <label className="text-sm font-medium text-[#212529]">Attachments</label>
              {formData.attachments.length > 0 && (
                <div className="space-y-2">
                  {formData.attachments.map((file, index) => (
                    <div key={index} className="flex items-center justify-between p-2 bg-gray-50 rounded">
                      <div className="flex items-center gap-2">
                        <FileText className="h-4 w-4 text-[#6C757D]" />
                        <span className="text-sm">{file}</span>
                      </div>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => removeAttachment(index)}
                        className="text-red-600"
                      >
                        <X className="h-4 w-4" />
                      </Button>
                    </div>
                  ))}
                </div>
              )}
              <Button
                variant="outline"
                size="sm"
                onClick={addAttachment}
                className="border-gray-200"
              >
                <Paperclip className="h-4 w-4 mr-2" />
                Add Attachment
              </Button>
              <p className="text-xs text-[#6C757D]">
                Note: File uploads are simulated. In production, actual files would be uploaded.
              </p>
            </div>
          </div>

          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setCreateDialog(false)}>
              Cancel
            </Button>
            <Button
              variant="outline"
              onClick={() => handleCreate(false)}
              disabled={submitting}
            >
              <Clock className="h-4 w-4 mr-2" />
              Save as Draft
            </Button>
            <Button
              className="bg-[#1D2D2A] hover:bg-[#243D37] text-white"
              onClick={() => handleCreate(true)}
              disabled={submitting}
              data-testid="publish-btn"
            >
              <Send className="h-4 w-4 mr-2" />
              {submitting ? 'Publishing...' : 'Publish Now'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* View Notice Dialog */}
      <Dialog open={viewDialog.open} onOpenChange={(open) => {
        if (!open) setViewDialog({ open: false, notice: null });
      }}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          {viewDialog.notice && (
            <>
              <DialogHeader>
                <DialogTitle className="font-['Manrope']">{viewDialog.notice.title}</DialogTitle>
                <DialogDescription>
                  Published: {formatDate(viewDialog.notice.published_date)}
                </DialogDescription>
              </DialogHeader>

              {/* Analytics */}
              <div className="grid grid-cols-3 gap-4 py-4 border-b">
                <div className="text-center">
                  <p className="text-2xl font-bold text-[#212529]">{viewDialog.notice.total_recipients}</p>
                  <p className="text-xs text-[#6C757D]">Recipients</p>
                </div>
                <div className="text-center">
                  <p className="text-2xl font-bold text-green-600">{viewDialog.notice.read_count}</p>
                  <p className="text-xs text-[#6C757D]">Read</p>
                </div>
                <div className="text-center">
                  <p className="text-2xl font-bold text-[#212529]">
                    {viewDialog.notice.total_recipients > 0 
                      ? Math.round((viewDialog.notice.read_count / viewDialog.notice.total_recipients) * 100)
                      : 0}%
                  </p>
                  <p className="text-xs text-[#6C757D]">Read Rate</p>
                </div>
              </div>

              {/* Content Preview */}
              <div 
                className="prose prose-sm max-w-none py-4"
                dangerouslySetInnerHTML={{ __html: viewDialog.notice.content_html }}
              />

              {/* Attachments */}
              {viewDialog.notice.attachments?.length > 0 && (
                <div className="pt-4 border-t">
                  <h4 className="text-sm font-semibold mb-2">Attachments</h4>
                  <div className="space-y-1">
                    {viewDialog.notice.attachments.map((att) => (
                      <div key={att.id} className="flex items-center gap-2 text-sm text-[#6C757D]">
                        <FileText className="h-4 w-4" />
                        {att.file_name}
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialog.open} onOpenChange={(open) => {
        if (!open) setDeleteDialog({ open: false, notice: null });
      }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Delete Notice</DialogTitle>
            <DialogDescription>
              Are you sure you want to delete "{deleteDialog.notice?.title}"? This action cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteDialog({ open: false, notice: null })}>
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={handleDelete}
            >
              Delete
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminNoticesPage;
